import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';

declare var KTMenu: any;

@Component({
  selector: 'app-pending-invoices',
  templateUrl: './pending-invoices.component.html',
  styleUrls: ['./pending-invoices.component.scss']
})
export class PendingInvoicesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Expose Math to template
  Math = Math;

  // Data properties
  vehicles: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  selectedVehicles: string[] = [];

  constructor(
    private _vehicleService: VehicleService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVehicles();
  }

  ngAfterViewInit(): void {
    this.refreshMenus();
  }

  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadVehicles(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    filters.is_sold = "false";
    this.subscriptions.push(
      this._vehicleService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.vehicles = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.vehicles = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading vehicles:', error);
          this.vehicles = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadVehicles(page, this.searchTerm);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadVehicles(1, searchTerm);
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  formatPrice(price: number | string, currency: string = 'JPY'): string {
    if (!price) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';

    if (currency === 'USD') {
      return '$' + numPrice.toLocaleString('en-US', { minimumFractionDigits: 0 });
    } else {
      return '¥' + numPrice.toLocaleString('ja-JP', { minimumFractionDigits: 0 });
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  calculateAge(yardDate: string): string {
    if (!yardDate) return '-';
    try {
      const yardDateObj = new Date(yardDate);
      const today = new Date();
      const timeDifference = today.getTime() - yardDateObj.getTime();
      const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));

      if (daysDifference < 0) return '0 days';
      if (daysDifference === 0) return 'Today';
      if (daysDifference === 1) return '1 day';
      return `${daysDifference} days`;
    } catch {
      return '-';
    }
  }

  // Checkbox selection methods
  toggleSelectAll(event: any): void {
    if (event.target.checked) {
      this.selectedVehicles = this.vehicles.map(v => v._id);
    } else {
      this.selectedVehicles = [];
    }
  }

  toggleVehicleSelection(vehicleId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedVehicles.includes(vehicleId)) {
        this.selectedVehicles.push(vehicleId);
      }
    } else {
      this.selectedVehicles = this.selectedVehicles.filter(id => id !== vehicleId);
    }
  }

  isAllSelected(): boolean {
    return this.vehicles.length > 0 && this.selectedVehicles.length === this.vehicles.length;
  }

  isIndeterminate(): boolean {
    return this.selectedVehicles.length > 0 && this.selectedVehicles.length < this.vehicles.length;
  }

  isVehicleSelected(vehicleId: string): boolean {
    return this.selectedVehicles.includes(vehicleId);
  }

  // Copy selected stock numbers to clipboard
  copySelectedStockNumbers(): void {
    const selectedCarNos = this.vehicles
      .filter(v => this.selectedVehicles.includes(v._id))
      .map(v => v.car_no)
      .filter(carNo => carNo) // Filter out null/undefined values
      .join(',');

    if (selectedCarNos) {
      navigator.clipboard.writeText(selectedCarNos).then(() => {
        // Show success message (you can use your toast/notification service here)
        alert('Stock numbers copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard');
      });
    }
  }

}
